<script setup lang="ts">
import { Page, Section, Button } from "@/components/Ui";
import { BaseAffiliate } from "@/components/Sections";
import {
  BlockGetCodesToBrowser,
  BlockCardsAtHand,
  BlockManageAccount,
  BlockCardAfterSignupV2,
  BlockNoLimits,
  BlockCryptoTopUp,
} from "@/components/Blocks";
import { onMounted } from "vue";
import { onEvent, onLink, onDashboard } from "@/helpers/events";
import config from "@/config/env";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const onSetupExtHandler = (location: string) => {
  onLink(
    "setup chrome extension",
    config.chromeExtensionUrl,
    {
      "cta type": "button",
      "cta location": location,
      "cta text": "Set up extension",
    },
    false
  );

  setTimeout(() => {
    onDashboard(
      "setup chrome extension",
      "/login",
      {
        "cta type": "button",
        "cta location": location,
        "cta text": "Set up extension",
      },
      {},
      true
    );
  }, 100);
};
onMounted(() => {
  onEvent("extension page viewed", null, {
    "product name": "extension"
  });
});

useSeoMeta({
  title: t("page.extension.seo.title"),
  description: t("page.extension.seo.description"),
  ogTitle: t("page.extension.seo.title"),
  ogDescription: t("page.extension.seo.description")
});
</script>

<template>
  <Page :class="$style.root">
    <Section>
      <div :class="$style.head">
        <h1 :class="[$style[`title`], $style[`title-${$i18n.locale}`]]">{{ $t("ext.pageTitle") }}</h1>
        <p>
          {{ $t("ext.pageSubTitle") }}
        </p>
        <Button
          :title="$t('btn.setUpExtension')"
          icon="chrome-color"
          icon-pos="left"
          :class="`${[$style.extBtn]} ${[$style['head-button']]}`"
          @click="onSetupExtHandler('Hero: Extension page')"
        />
        <nuxt-img
          src="/img/hero-ext.png"
          width="1200"
          height="756"
          format="webp"
          sizes="sm:100vw md:756px lg:1200px"
          aria-label="Cardholder in your browser"
        />
      </div>
    </Section>
    <Section :title-class="$style['root-h2']">
      <template #title>
        {{ $t("ext.universalPaymentTool") }}
      </template>
      <div :class="$style.features">
        <BlockGetCodesToBrowser>
          <template #action>
            <Button
              :title="$t('btn.setUpExtension')"
              icon="chrome-color"
              icon-pos="left"
              color="white"
              :class="$style.extBtn"
              @click="
                onSetupExtHandler('Block: Get 3D-S codes to your browser')
              "
            />
          </template>
        </BlockGetCodesToBrowser>
        <BlockCardsAtHand>
          <template #action>
            <Button
              :title="$t('btn.setUpExtension')"
              icon="chrome-color"
              icon-pos="left"
              :class="$style.extBtn"
              @click="onSetupExtHandler('Block: Your cards are always at hand')"
            />
          </template>
        </BlockCardsAtHand>
        <BlockManageAccount>
          <template #action>
            <Button
              :title="$t('btn.setUpExtension')"
              icon="chrome-color"
              icon-pos="left"
              :class="$style.extBtn"
              @click="onSetupExtHandler('Block: Manage your PST account')"
            />
          </template>
        </BlockManageAccount>
      </div>
    </Section>
    <Section :title-class="$style['root-h2']">
      <template #title>
        {{ $t("ext.convenienceAtAnyBudget") }}
      </template>
      <div :class="$style.features">
        <BlockCardAfterSignupV2>
          <template #action>
            <Button
              :title="$t('btn.setUpExtension')"
              icon="chrome-color"
              icon-pos="left"
              :class="$style.extBtn"
              @click="onSetupExtHandler('Block: First card after signing up')"
            />
          </template>
        </BlockCardAfterSignupV2>
        <BlockNoLimits>
          <template #action>
            <Button
              :title="$t('btn.setUpExtension')"
              icon="chrome-color"
              icon-pos="left"
              :class="$style.extBtn"
              @click="onSetupExtHandler('Block: No limits any more')"
            />
          </template>
        </BlockNoLimits>
        <BlockCryptoTopUp>
          <template #action>
            <Button
              :title="$t('btn.setUpExtension')"
              icon="chrome-color"
              icon-pos="left"
              color="white"
              :class="$style.extBtn"
              @click="onSetupExtHandler('Block: Top up by crypto and more')"
            />
          </template>
        </BlockCryptoTopUp>
      </div>
    </Section>
    <BaseAffiliate />
  </Page>
</template>

<style lang="scss" module>
.root {
  @apply flex flex-col gap-20 pt-[100px] mt-[70px] md:mt-[90px];
  &-h2 {
    @apply font-bold text-[36px] leading-none text-center;
  }
}

.features {
  @apply grid grid-cols-1 lg:grid-cols-3 gap-x-8 xl:gap-x-10 gap-y-[10px] w-full;
}

.extBtn {
  @apply w-full text-[18px] xs:text-xl;

  & svg {
    @apply h-6 w-6 min-w-[24px];
  }
}

.head {
  @apply flex flex-col items-center text-center;

  & p {
    @apply text-neutral-800 text-2xl mb-10;
  }

  &-button {
    @apply max-w-fit mb-10;
  }

  & img {
    @apply w-[756px];
  }
}

.title {
  @apply text-neutral-800 font-black leading-none mb-6;
  @apply text-[52px] md:text-[60px];
  &-en,
&-id,
&-da,
&-et,
&-hu,
&-lt,
&-nb,
&-ro,
&-sl,
&-cs,
&-el,
&-fi,
&-lv,
&-nl,
&-sk,
&-sv,
&-he,
  &-be,
  &-it,
  &-pt,
  &-ua,
  &-es,
  &-fr,
  &-ja,
  &-zh,
  &-th,
  &-hi,
  &-ar,
  &-bn,
  &-ko,
  &-vi,
  &-sw{
    @apply text-[52px] md:text-[60px];
  }
  &-zh {
    @apply leading-[1.2];
  }
  &-pl,
  &-tr {
    @apply text-[42px] xxs:text-[52px] md:text-[60px];
  }
  &-de {
    @apply text-[40px] xxs:text-[46px] xs:text-[48px] md:text-[60px];
  }
  &-bg {
    @apply text-[36px] xxs:text-[42px] xs:text-[48px] md:text-[60px];
  }
  &-ru {
    @apply text-[52px] md:text-[60px];
  }
}
</style>
